import { useId, useState, useEffect } from "react";

import { LuPlus } from "react-icons/lu";
import { VscEye, VscEyeClosed } from "react-icons/vsc";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { 
  MdFormatBold, 
  MdFormatItalic, 
  MdFormatUnderlined, 
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdLink,
  MdFormatQuote
} from "react-icons/md";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { ErrorLabel } from "./ErrorLabel";

interface BaseCustomInputProps {
  label?: string;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  name?: string;
  showPasswordToggle?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
  addButtonDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  value?: string | number;
  infoIcon?: {
    message: string;
    icon?: React.ReactNode;
  };
}

interface EditorInputProps extends BaseCustomInputProps {
  as: "editor";
  onChange?: (value: string) => void;
}

interface StandardInputProps extends BaseCustomInputProps, Omit<React.ComponentProps<typeof Input>, "onChange" | "value"> {
  as?: "input" | "textarea";
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

type CustomInputProps = EditorInputProps | StandardInputProps;

export default function CustomInput(props: CustomInputProps) {
  const {
    label,
    error,
    containerClassName,
    labelClassName,
    inputClassName,
    name,
    showPasswordToggle,
    as = "input",
    showAddButton = false,
    onAddClick,
    addButtonDisabled = false,
    autoFocus = false,
    placeholder,
    value,
    infoIcon,
  } = props;
  
  const className = "className" in props ? props.className : undefined;
  const id = "id" in props ? props.id : undefined;
  const type = "type" in props ? props.type : undefined;
  const generatedId = useId();
  const inputId = id || generatedId;
  const [reveal, setReveal] = useState(false);

  // Rich Text / HTML Mode
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  // Filter out showPasswordToggle from restProps to prevent it from being passed to DOM elements
  const getRestProps = () => {
    if ("as" in props && props.as === "editor") {
      return {};
    }
    // Exclude custom props from being spread onto DOM elements
    const {type, showPasswordToggle, showAddButton, onAddClick, addButtonDisabled, infoIcon, ...rest } = props as StandardInputProps;
    return rest;
  };
  const restProps = getRestProps();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline cursor-pointer",
        },
      }),
    ],
    content: (as === "editor" ? value as string : undefined) || "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      if (
        !isHtmlMode &&
        as === "editor" &&
        "onChange" in props &&
        props.onChange
      ) {
        const html = editor.getHTML();
        (props.onChange as (value: string) => void)(html);
      }
    },
    editorProps: {
      attributes: {
        class: `focus:outline-none`,
      },
    },
  });

  useEffect(() => {
  if (
    editor &&
    as === "editor" &&
    !isHtmlMode &&
    value !== undefined &&
    editor.getHTML() !== value
  ) {
    editor.commands.setContent(value as string || "");
  }
}, [editor, as, value, isHtmlMode]);

  useEffect(() => {
    if (editor && as === "editor" && autoFocus) {
      editor.commands.focus();
    }
  }, [editor, autoFocus, as]);

  return (
    <div className={containerClassName || ""}>
      {label && (
        <div className="flex items-center gap-1.5 mb-2">
          <Label
            htmlFor={inputId}
            className={`text-body-semibold/md text-text-900 ${labelClassName || ""}`}>
            {label}
          </Label>
          {infoIcon && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-text-600 hover:text-text-900 transition-colors"
                  aria-label="Information"
                >
                  {infoIcon.icon || null}
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-white text-text-600 border border-outline-200 shadow-soft-3">
                <p className="">{infoIcon.message}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      {type === "tel" ? (
        <div className="relative">
          <div className="text-text-600 text-body-normal/md absolute top-1/2 left-3.5 flex -translate-y-1/2 items-center">
            <span>+91</span>
            <div className="bg-outline-600 mx-3 h-4 w-px"></div>
          </div>
          <Input
            id={inputId}
            name={name}
            type="tel"
            autoFocus={autoFocus}
            className={`bg-background-0 border-outline-600 text-text-900 placeholder:text-text-600 text-body-normal/md h-12 rounded-lg border pr-4 pl-16 ${error ? "!border-error-500 focus:!border-error-500" : ""} ${inputClassName || ""} ${className || ""}`}
            placeholder="Enter Phone Number"
            onChange={"onChange" in props ? (props.onChange as React.ChangeEventHandler<HTMLInputElement>) : undefined}
            {...(restProps as Omit<React.ComponentProps<typeof Input>, "onChange" | "value">)}
          />
        </div>
      ) : as === "textarea" ? (
        <Textarea
          id={inputId}
          name={name}
          autoFocus={autoFocus}
          value={value}
          onChange={"onChange" in props ? (props.onChange as React.ChangeEventHandler<HTMLTextAreaElement>) : undefined}
          className={`bg-background-0 border-outline-600 text-text-900 placeholder:text-text-600 text-body-normal/md min-h-[100px] rounded-lg border px-4 py-3 break-words break-all ${error ? "!border-error-500 focus:!border-error-500" : ""} ${inputClassName || ""} ${className || ""}`}
          {...(restProps as React.ComponentProps<typeof Textarea>)}
        />
      ) : as === "editor" ? (
        <div
          className={`relative bg-background-0 border-outline-600 text-text-900 text-body-normal/md min-h-[100px] rounded-lg border ${error ? "!border-error-500 focus-within:!border-error-500" : ""} ${inputClassName || ""} ${className || ""}`}
        >
          {editor && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-1 border-b border-outline-600 px-3 py-2">

                {/* RICH/HTML Toggler */}
                <button
                  type="button"
                  onClick={() => setIsHtmlMode(v => !v)}
                  className={`w-16 mr-2 p-2 rounded hover:bg-outline-100 disabled:opacity-50 disabled:cursor-not-allowed text-text-900 text-body-normal/sm bg-outline-200 `}
                >
                  {isHtmlMode ? "Text" : "Html"}
                </button>

                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  disabled={!editor.can().chain().focus().toggleBold().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 disabled:opacity-50 disabled:cursor-not-allowed ${editor.isActive("bold") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Bold"
                >
                  <MdFormatBold size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  disabled={!editor.can().chain().focus().toggleItalic().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 disabled:opacity-50 disabled:cursor-not-allowed ${editor.isActive("italic") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Italic"
                >
                  <MdFormatItalic size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  disabled={!editor.can().chain().focus().toggleUnderline().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 disabled:opacity-50 disabled:cursor-not-allowed ${editor.isActive("underline") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Underline"
                >
                  <MdFormatUnderlined size={18} />
                </button>
                <div className="w-px h-6 bg-outline-600 mx-1" />
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 ${editor.isActive("bulletList") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Bullet List"
                >
                  <MdFormatListBulleted size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 ${editor.isActive("orderedList") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Numbered List"
                >
                  <MdFormatListNumbered size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={`p-1.5 rounded hover:bg-outline-100 ${editor.isActive("blockquote") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Quote"
                >
                  <MdFormatQuote size={18} />
                </button>
                <div className="w-px h-6 bg-outline-600 mx-1" />
                <button
                  type="button"
                  onClick={() => {
                    const url = window.prompt("Enter URL:");
                    if (url) {
                      editor.chain().focus().setLink({ href: url }).run();
                    }
                  }}
                  className={`p-1.5 rounded hover:bg-outline-100 ${editor.isActive("link") ? "bg-outline-200 text-text-900" : "text-text-600"}`}
                  aria-label="Add Link"
                >
                  <MdLink size={18} />
                </button>
                {editor.isActive("link") && (
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().unsetLink().run()}
                    className="p-1.5 rounded hover:bg-outline-100 text-text-600"
                    aria-label="Remove Link"
                  >
                    Unlink
                  </button>
                )}
              </div>
              
              {/* Editor Content */}
              <div className="px-4 py-3 relative">
                {!editor.getText() && placeholder && (
                  <div className="absolute top-3 left-4 pointer-events-none text-text-600 text-body-normal/md">
                    {placeholder}
                  </div>
                )}

                {!isHtmlMode ? (
                <EditorContent
                  editor={editor}
                  className="[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px] [&_.ProseMirror]:text-text-900 [&_.ProseMirror]:text-body-normal/md [&_.ProseMirror_placeholder]:text-text-600 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-text-600 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:cursor-pointer [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-6 [&_.ProseMirror_ol]:my-2 [&_.ProseMirror_li]:ml-2 [&_.ProseMirror_li]:my-1 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-outline-300 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:my-2 [&_.ProseMirror_blockquote]:italic"
                />
              ) : (
                <Textarea
                  value={typeof value === "string" ? value : ""}
                  onChange={(e) => {
                    if ("onChange" in props && props.onChange) {
                      (props.onChange as (value: string) => void)(e.target.value);
                    }
                  }}
                  className="min-h-[60px] max-h-[400px] font-mono border-0 p-0 text-text-600 text-body-normal/md"
                  style={{lineHeight: 1.5}}
                />
              )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="relative">
          <Input
            id={inputId}
            name={name}
            type={showPasswordToggle ? (reveal ? "text" : "password") : type}
            autoFocus={autoFocus}
            className={`bg-background-0 border-outline-600 text-text-900 placeholder:text-text-600 text-body-normal/md h-12 rounded-lg border px-4 ${showAddButton ? "pr-12" : ""} ${error ? "!border-error-500 focus:!border-error-500" : ""} ${inputClassName || ""} ${className || ""}`}
            onChange={"onChange" in props ? (props.onChange as React.ChangeEventHandler<HTMLInputElement>) : undefined}
            {...(restProps as Omit<React.ComponentProps<typeof Input>, "onChange" | "value">)}
          />
          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="text-password-eye-color absolute top-1/2 right-3 -translate-y-1/2"
              aria-label={reveal ? "Hide password" : "Show password"}>
              {reveal ? (
                <VscEyeClosed height={10} width={15.5} />
              ) : (
                <VscEye height={10} width={15.5} />
              )}
            </button>
          )}
          {showAddButton && (
            <button
              type="button"
              onClick={onAddClick}
              disabled={addButtonDisabled}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Add item">
              <LuPlus className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {error && <ErrorLabel error={error} />}
    </div>
  );
}
